import React, { useEffect, useState } from 'react';
import { CreditCard, Save, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { toast } from '../../hooks/use-toast';
import adminApi from '../../lib/adminApi';

const Badge = ({ ok, children }) => (
  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs ${ok ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-yellow-500/10 text-yellow-300 border border-yellow-500/20'}`}>
    {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
    {children}
  </span>
);

const PaymentSettings = () => {
  const [settings, setSettings] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [savingPricing, setSavingPricing] = useState(false);
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const res = await adminApi.get('/api/admin/settings/payments');
    setSettings(res.data.settings);
    setStatus(res.data.status);
    const pr = await adminApi.get('/api/admin/settings/pricing');
    setPricing(pr.data.settings);
  };

  const savePricing = async () => {
    setSavingPricing(true);
    try {
      const res = await adminApi.put('/api/admin/settings/pricing', {
        aedRate: parseFloat(pricing.aedRate),
        aedRounding: parseInt(pricing.aedRounding, 10),
      });
      setPricing(res.data.settings);
      toast({ title: 'Taux enregistré', description: 'Les prix en AED sont recalculés immédiatement sur le site.' });
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || 'Enregistrement impossible', variant: 'destructive' });
    }
    setSavingPricing(false);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await adminApi.put('/api/admin/settings/payments', settings);
      setSettings(res.data.settings);
      setStatus(res.data.status);
      toast({ title: 'Paiements mis à jour', description: 'Les changements sont actifs immédiatement sur le site.' });
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || 'Enregistrement impossible', variant: 'destructive' });
    }
    setSaving(false);
  };

  if (!settings || !status) {
    return <div className="flex items-center gap-2 text-gray-400"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>;
  }

  return (
    <div data-testid="admin-payments">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Paiements</h1>
        <p className="text-gray-400 text-sm">
          Activez ou coupez chaque moyen de paiement du tunnel de commande. Les clés restent côté serveur : elles ne sont ni affichées ici, ni envoyées au navigateur.
        </p>
      </div>

      <div className="space-y-6 max-w-3xl">
        {/* Stripe */}
        <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
          <div className="flex items-start justify-between gap-4 mb-5">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 bg-purple-600/20 border border-purple-500/30 rounded-lg flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h2 className="text-white text-lg font-semibold">Carte bancaire — Stripe</h2>
                <p className="text-gray-500 text-sm">Visa, Mastercard, Amex, Apple Pay, Google Pay</p>
              </div>
            </div>
            <Switch
              checked={settings.stripeEnabled}
              onCheckedChange={(v) => setSettings({ ...settings, stripeEnabled: v })}
              data-testid="toggle-stripe"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge ok={status.stripeConfigured}>{status.stripeConfigured ? 'Clé serveur détectée' : 'Clé serveur absente'}</Badge>
            <Badge ok={status.stripeMode === 'live'}>{status.stripeMode === 'live' ? 'Mode production' : 'Mode test'}</Badge>
          </div>
          {status.stripeMode !== 'live' && (
            <p className="text-gray-500 text-xs mt-4 leading-relaxed" data-testid="stripe-live-note">
              En mode test, utilisez la carte <span className="text-gray-300">4242 4242 4242 4242</span>, une date future et n'importe quel CVC : aucun argent n'est débité.
              Pour encaisser réellement, il faut passer le compte Stripe en production depuis l'onglet Paiements d'Emergent — la clé de production est alors installée automatiquement côté serveur.
            </p>
          )}
        </div>

        {/* PayPal */}
        <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
          <div className="flex items-start justify-between gap-4 mb-5">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 bg-blue-600/20 border border-blue-500/30 rounded-lg flex items-center justify-center">
                <span className="text-blue-400 font-bold text-lg">P</span>
              </div>
              <div>
                <h2 className="text-white text-lg font-semibold">PayPal</h2>
                <p className="text-gray-500 text-sm">Compte PayPal et carte via PayPal</p>
              </div>
            </div>
            <Switch
              checked={settings.paypalEnabled}
              onCheckedChange={(v) => setSettings({ ...settings, paypalEnabled: v })}
              data-testid="toggle-paypal"
            />
          </div>

          <div className="flex flex-wrap gap-2 mb-5">
            <Badge ok={status.paypalConfigured}>{status.paypalConfigured ? 'Identifiants serveur détectés' : 'Identifiants serveur absents'}</Badge>
            <Badge ok={status.paypalMode === 'live'}>{status.paypalMode === 'live' ? 'Mode production' : 'Mode test (sandbox)'}</Badge>
          </div>

          <div className="mb-5 p-3 rounded-lg bg-white/[0.02] border border-white/5">
            <p className="text-gray-400 text-xs leading-relaxed">
              Les identifiants PayPal et la clé Stripe sont lus dans les variables d'environnement du serveur : cet écran affiche seulement leur état.
              L'état ci-dessus correspond à <span className="text-gray-300">cet environnement</span> — la preview et le site en ligne ont chacun leurs propres variables,
              donc l'affichage peut différer entre les deux.
            </p>
          </div>

          <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">Environnement PayPal</p>
          <div className="flex flex-wrap gap-2">
            {[{ k: null, l: 'Automatique (serveur)' }, { k: 'sandbox', l: 'Forcer test' }, { k: 'live', l: 'Forcer production' }].map(({ k, l }) => (
              <button
                key={String(k)}
                onClick={() => setSettings({ ...settings, paypalMode: k })}
                className={`px-4 py-2 rounded-full text-sm border transition-colors ${(settings.paypalMode || null) === k ? 'bg-white text-black border-white' : 'text-gray-300 border-gray-700 hover:border-gray-500'}`}
                data-testid={`paypal-mode-${k || 'auto'}`}
              >
                {l}
              </button>
            ))}
          </div>
          <p className="text-gray-500 text-xs mt-3" data-testid="paypal-effective-mode">
            Environnement réellement utilisé : <span className="text-gray-300">{status.paypalMode === 'live' ? 'production' : 'test (sandbox)'}</span>
            {settings.paypalMode ? ' (forcé ici)' : ` (suit le serveur : ${status.paypalModeEnv})`}
          </p>

          {!status.paypalConfigured && (
            <p className="text-yellow-300/90 text-xs mt-4 leading-relaxed" data-testid="paypal-missing-note">
              Les identifiants PayPal présents sur le serveur sont incomplets ou factices. Pour activer PayPal, il faut renseigner
              <span className="text-white"> PAYPAL_CLIENT_ID</span> et <span className="text-white">PAYPAL_SECRET</span> dans les variables
              d'environnement du backend (Client ID et Secret de votre application sur developer.paypal.com → My Apps &amp; Credentials).
              Le mode choisi ci-dessus doit correspondre au couple d'identifiants installé.
            </p>
          )}
        </div>

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} className="bg-orange-600 hover:bg-orange-500 px-6" data-testid="save-payments">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Enregistrer
          </Button>
        </div>

        {/* Prix par zone */}
        <div className="bg-gray-900 rounded-xl p-6 border border-gray-800" data-testid="admin-pricing">
          <h2 className="text-white font-semibold mb-1">Prix, TVA et devise par zone</h2>
          <p className="text-gray-400 text-xs mb-5 leading-relaxed">
            France : EUR, TVA 20 % — Italie : EUR, TVA 22 % — Émirats : AED, TVA 5 % (affichage seul, commande via le revendeur).
            Le prix HT en EUR de chaque kit se modifie dans Produits ; un « prix AED » saisi sur un kit remplace le calcul par le taux.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block text-sm text-gray-300">
              Taux EUR → AED
              <input
                type="number" step="0.01" min="0.01"
                value={pricing?.aedRate ?? ''}
                onChange={(e) => setPricing({ ...pricing, aedRate: e.target.value })}
                className="w-full mt-1 bg-gray-800 border border-gray-700 text-white rounded-md px-3 py-2 text-sm"
                data-testid="aed-rate-input"
              />
            </label>
            <label className="block text-sm text-gray-300">
              Arrondi commercial (AED)
              <input
                type="number" step="1" min="0"
                value={pricing?.aedRounding ?? ''}
                onChange={(e) => setPricing({ ...pricing, aedRounding: e.target.value })}
                className="w-full mt-1 bg-gray-800 border border-gray-700 text-white rounded-md px-3 py-2 text-sm"
                data-testid="aed-rounding-input"
              />
              <span className="block text-gray-500 text-xs mt-1">50 = montants arrondis au multiple de 50 AED.</span>
            </label>
          </div>
          <div className="flex justify-end mt-5">
            <Button onClick={savePricing} disabled={savingPricing || !pricing} className="bg-orange-600 hover:bg-orange-500 px-6" data-testid="save-pricing">
              {savingPricing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Enregistrer le taux
            </Button>
          </div>
        </div>

        <div className="bg-white/[0.02] border border-white/5 rounded-xl p-5">
          <p className="text-gray-400 text-xs leading-relaxed">
            Le montant à payer est toujours calculé côté serveur d'après la commande : il ne peut pas être modifié depuis le navigateur.
            Chaque paiement confirmé met la commande en « Confirmée / payée » avec son moyen de paiement et son identifiant de transaction,
            visibles dans Commandes.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSettings;
